import { AppError, IdSchema } from "@hisani/core";
import { isStopwatchSectionalDistanceMeters } from "@hisani/stopwatch";
import { z } from "zod";
import { InvoiceStatusSchema, STABLE_ERROR_CODES } from "./contracts.js";
import { EntityRoleSchema, StableFeatureSchema, FeatureAccessLevelSchema, } from "./domain/membership/entity-roles.js";
import { HorseBreedSchema, HorseColorSchema, HorseSexSchema, RacingCountryCodeSchema, } from "./domain/horses/horse-details.js";
import { HorseLifecycleStatusSchema } from "./domain/horses/horse-lifecycle.js";
import { HemisphereSchema } from "./domain/horses/racing-age.js";
import { StablePublicIdSchema } from "./domain/stable/public-id.js";
import { CARE_RECORD_VALUE_MAX, CARE_RECORD_VALUE_MIN, MassUnitSchema, TemperatureUnitSchema, } from "./domain/care-records/care-record.js";
import { LOT_NAME_MAX_LENGTH } from "./domain/training/lot.js";
import { DistanceUnitSchema, TRAINING_DISTANCE_VALUE_MAX, TRAINING_DISTANCE_VALUE_MIN, TRAINING_DURATION_MILLISECONDS_MAX, TRAINING_INSTRUCTIONS_MAX_LENGTH, TRAINING_OUTCOME_NOTES_MAX_LENGTH, WORK_TYPE_MAX_LENGTH, } from "./domain/training/training-entry.js";
import { SEALED_RECORD_DOCUMENT_CONTENT_TYPES, SEALED_RECORD_DOCUMENT_FILENAME_MAX_LENGTH, SEALED_RECORD_DOCUMENT_MAX_COUNT, SEALED_RECORD_DOCUMENT_MAX_SIZE_BYTES, SEALED_RECORD_NOTES_MAX_LENGTH, matchesSealedRecordDocumentExtension, } from "./domain/records/sealed-timeline-record.js";
import { EXTRACTION_PDF_CONTENT_TYPES, EXTRACTION_PDF_MAX_SIZE_BYTES, EXTRACTION_PHOTO_CONTENT_TYPES, EXTRACTION_PHOTO_FILENAME_MAX_LENGTH, EXTRACTION_PHOTO_MAX_SIZE_BYTES, matchesExtractionPdfExtension, matchesExtractionPhotoExtension, } from "./domain/extraction/extraction-kind.js";
import { HORSE_SCAN_KINDS } from "./domain/extraction/horse-scan-extraction.js";
import { VACCINATION_SCAN_KINDS } from "./domain/extraction/vaccination-scan-extraction.js";
import { BILL_SCAN_KINDS } from "./domain/extraction/bill-scan-extraction.js";
import { VaccineTypeCodeSchema } from "./domain/vaccinations/vaccine-type.js";
import { ExpenseCategoryCodeSchema } from "./domain/accounting/expense-category.js";
import { AmountMinorSchema, CurrencyCodeSchema, } from "./domain/accounting/money.js";
import { BillAllocationInputSchema } from "./domain/accounting/bill-allocation.js";
import { JOB_POSTING_DESCRIPTION_MAX_LENGTH, JOB_POSTING_TITLE_MAX_LENGTH, } from "./domain/hire/application.js";
import { RESUME_BIO_MAX_LENGTH, RESUME_SKILLS_MAX_COUNT, RESUME_SKILL_MAX_LENGTH, } from "./domain/hire/resume.js";
export function stableNotFound() {
    return new AppError({
        code: STABLE_ERROR_CODES.notFound,
        message: "The Stable or horse could not be found.",
    });
}
export function stableForbidden(message = "You do not have permission to perform this action.") {
    return new AppError({ code: STABLE_ERROR_CODES.forbidden, message });
}
export function stableConflict(message = "This request conflicts with the current state.") {
    return new AppError({ code: STABLE_ERROR_CODES.conflict, message });
}
export function stableInvalidTransition(message) {
    return new AppError({ code: STABLE_ERROR_CODES.invalidTransition, message });
}
export function stableCapacityLimit() {
    return new AppError({
        code: STABLE_ERROR_CODES.capacityLimit,
        message: "This Stable has reached its active-horse limit for its current tier. Upgrade to add more horses.",
    });
}
export function stableRateLimited() {
    return new AppError({
        code: STABLE_ERROR_CODES.rateLimited,
        message: "Too many Stable ID lookups. Try again later.",
    });
}
export function stableUploadUnavailable() {
    return new AppError({
        code: STABLE_ERROR_CODES.unavailable,
        message: "File uploads are temporarily unavailable.",
    });
}
export function stableExtractionUnavailable() {
    return new AppError({
        code: STABLE_ERROR_CODES.unavailable,
        message: "Photo scanning is temporarily unavailable.",
    });
}
export const createStableSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    countryCode: RacingCountryCodeSchema.nullable().optional(),
})
    .strict();
export const inviteStaffSchema = z
    .object({
    stableId: IdSchema,
    contactPointType: z.enum(["EMAIL", "PHONE"]),
    contactPointValue: z.string().trim().min(3).max(320),
    entityRole: EntityRoleSchema,
})
    .strict();
export const removeStaffSchema = z
    .object({ stableId: IdSchema, membershipId: IdSchema })
    .strict();
export const changeEntityRoleSchema = z
    .object({
    stableId: IdSchema,
    membershipId: IdSchema,
    newRole: EntityRoleSchema,
})
    .strict();
export const setPermissionOverrideSchema = z
    .object({
    stableId: IdSchema,
    membershipId: IdSchema,
    feature: StableFeatureSchema,
    accessLevel: FeatureAccessLevelSchema.nullable(),
})
    .strict();
export const lookupStablePublicIdSchema = z
    .object({ publicId: StablePublicIdSchema })
    .strict();
export const requestEntityOwnerTransferSchema = z
    .object({ stableId: IdSchema, toHorsemanId: IdSchema })
    .strict();
export const respondEntityOwnerTransferSchema = z
    .object({ transferId: IdSchema, decision: z.enum(["ACCEPTED", "DECLINED"]) })
    .strict();
// --- Link An Owner ---
/** A query that looks like a phone number — `docs/products/stable.md`:
 * "search existing Horsemen by name or email (never phone)"; `COMPANY.md`
 * Trust & Safety: "a phone Contact Point can never be harvested for spam".
 * Mirrors the equivalent guard in `@hisani/admin`'s `searchAdminUsers`. */
const phoneLikeSearchPattern = /^\+?[\d\s().-]{7,}$/u;
/** Search must be at least 2 characters — a 1-character query against
 * `displayName ilike '%x%'` would scan far too broadly to be a meaningful
 * name/email match. */
export const searchHorsemenSchema = z
    .object({ stableId: IdSchema, query: z.string().trim().min(2).max(320) })
    .strict()
    .refine((value) => !phoneLikeSearchPattern.test(value.query), {
    message: "Search is by name or email only — phone search is not supported.",
    path: ["query"],
});
const ownerLinkTargetInputSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("HORSEMAN"), horsemanId: IdSchema }).strict(),
    z
        .object({
        kind: z.literal("INVITE"),
        contactPointType: z.enum(["EMAIL", "PHONE"]),
        contactPointValue: z.string().trim().min(3).max(320),
    })
        .strict(),
]);
export const requestOwnerLinkSchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema,
    clientMutationId: IdSchema,
    target: ownerLinkTargetInputSchema,
})
    .strict();
export const respondOwnerLinkRequestSchema = z
    .object({
    requestId: IdSchema,
    decision: z.enum(["ACCEPTED", "DECLINED"]),
    clientMutationId: IdSchema,
    expectedVersion: z.number().int().positive(),
})
    .strict();
export const cancelOwnerLinkRequestSchema = z
    .object({
    stableId: IdSchema,
    requestId: IdSchema,
    clientMutationId: IdSchema,
    expectedVersion: z.number().int().positive(),
})
    .strict();
export const changePrimaryOwnerSchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema,
    horsemanId: IdSchema,
    clientMutationId: IdSchema,
    expectedOwnerLinksVersion: z.number().int().positive(),
})
    .strict();
export const listOwnerLinksForHorseSchema = z
    .object({ stableId: IdSchema, horseId: IdSchema })
    .strict();
// --- Stable Transfer ---
export const requestStableTransferSchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema,
    clientMutationId: IdSchema,
    destinationPublicId: StablePublicIdSchema,
    ownerLinkHorsemanIdsToCarryForward: z.array(IdSchema).max(50).default([]),
})
    .strict();
export const listOutgoingStableTransferRequestsSchema = z
    .object({ stableId: IdSchema })
    .strict();
export const listIncomingStableTransferRequestsSchema = z
    .object({ stableId: IdSchema })
    .strict();
export const cancelStableTransferSchema = z
    .object({
    stableId: IdSchema,
    transferId: IdSchema,
    clientMutationId: IdSchema,
    expectedVersion: z.number().int().positive(),
})
    .strict();
export const respondStableTransferSchema = z
    .object({
    stableId: IdSchema,
    transferId: IdSchema,
    decision: z.enum(["ACCEPTED", "DECLINED"]),
    clientMutationId: IdSchema,
    expectedVersion: z.number().int().positive(),
})
    .strict();
const horseDateOfBirthSchema = z
    .object({
    year: z.number().int().min(1980).max(2100),
    month: z.number().int().min(1).max(12).nullable(),
    day: z.number().int().min(1).max(31).nullable(),
})
    .strict()
    .refine((value) => value.day === null || value.month !== null, {
    message: "Day of birth requires month of birth.",
    path: ["day"],
});
export const horseFieldsSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    breed: HorseBreedSchema,
    breedOtherLabel: z.string().trim().min(1).max(120).nullable(),
    sex: HorseSexSchema,
    dateOfBirth: horseDateOfBirthSchema,
    passportNumber: z.string().trim().min(1).max(64).nullable(),
    sire: z.string().trim().min(1).max(120).nullable(),
    dam: z.string().trim().min(1).max(120).nullable(),
    bredIn: RacingCountryCodeSchema.nullable(),
    color: HorseColorSchema.nullable(),
    barnNumber: z.string().trim().min(1).max(60).nullable(),
    hemisphereOverride: HemisphereSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.breed === "OTHER" && value.breedOtherLabel === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["breedOtherLabel"],
            message: "Breed label is required when Breed is Other.",
        });
    }
    if (value.breed !== "OTHER" && value.breedOtherLabel !== null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["breedOtherLabel"],
            message: "Breed label is only accepted when Breed is Other.",
        });
    }
    if (value.sex === "GELDING") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["sex"],
            message: "Gelding is recorded as a dated lifecycle event, not set on add/edit.",
        });
    }
});
export const addHorseSchema = z
    .object({
    stableId: IdSchema,
    fields: horseFieldsSchema,
    acknowledgeDuplicateWarning: z.boolean().default(false),
    /** Present when the submitted fields were prefilled from a horse-add
     * scan draft (`scanHorseDocument`) — links the draft's provenance
     * record to the created horse. Optional: manual entry never sets it. */
    scanExtractionId: IdSchema.nullable().default(null),
})
    .strict();
const HorseScanKindSchema = z.enum(HORSE_SCAN_KINDS);
export const beginHorseScanUploadSchema = z
    .object({
    stableId: IdSchema,
    id: IdSchema,
    filename: z
        .string()
        .trim()
        .min(1)
        .max(EXTRACTION_PHOTO_FILENAME_MAX_LENGTH),
    contentType: z.enum(EXTRACTION_PHOTO_CONTENT_TYPES),
    contentLength: z
        .number()
        .int()
        .positive()
        .max(EXTRACTION_PHOTO_MAX_SIZE_BYTES),
})
    .strict()
    .refine((value) => matchesExtractionPhotoExtension(value.contentType, value.filename), {
    message: "The file extension does not match its content type.",
    path: ["filename"],
});
export const scanHorseDocumentSchema = z
    .object({
    stableId: IdSchema,
    id: IdSchema,
    kind: HorseScanKindSchema,
    objectKey: z.string().trim().min(1).max(1024),
    contentType: z.enum(EXTRACTION_PHOTO_CONTENT_TYPES),
    contentLength: z
        .number()
        .int()
        .positive()
        .max(EXTRACTION_PHOTO_MAX_SIZE_BYTES),
})
    .strict();
export const updateHorseSchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema,
    expectedVersion: z.number().int().positive(),
    fields: horseFieldsSchema,
})
    .strict();
export const recordGeldingSchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema,
    expectedVersion: z.number().int().positive(),
    geldedOn: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, "geldedOn must be an ISO date (YYYY-MM-DD)."),
})
    .strict();
export const changeHorseLifecycleSchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema,
    expectedVersion: z.number().int().positive(),
    newStatus: HorseLifecycleStatusSchema,
})
    .strict();
export const getStableSchema = z.object({ stableId: IdSchema }).strict();
export const listHorsesSchema = z.object({ stableId: IdSchema }).strict();
export const getHorseSchema = z
    .object({ stableId: IdSchema, horseId: IdSchema })
    .strict();
// --- Temperature/Weight/Feed Records (Sealed, append-only) ---
const massSchema = z
    .object({
    value: z
        .number()
        .finite()
        .gt(CARE_RECORD_VALUE_MIN)
        .max(CARE_RECORD_VALUE_MAX),
    unit: MassUnitSchema,
})
    .strict();
const temperatureValueSchema = z
    .object({
    value: z
        .number()
        .finite()
        .gt(CARE_RECORD_VALUE_MIN)
        .max(CARE_RECORD_VALUE_MAX),
    unit: TemperatureUnitSchema,
})
    .strict();
const paginationCursorSchema = z
    .object({ at: z.coerce.date(), id: IdSchema })
    .strict();
const carePageSchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema,
    limit: z.number().int().min(1).max(100).default(50),
    cursor: paginationCursorSchema.nullable().default(null),
})
    .strict();
export const addWeightRecordSchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema,
    weight: massSchema,
    eventTime: z.coerce.date(),
})
    .strict();
export const listWeightRecordsSchema = carePageSchema;
export const addTemperatureRecordSchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema,
    temperature: temperatureValueSchema,
    eventTime: z.coerce.date(),
})
    .strict();
export const listTemperatureRecordsSchema = carePageSchema;
export const addFeedRecordSchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema,
    description: z.string().trim().min(1).max(200),
    amount: massSchema.nullable(),
    eventTime: z.coerce.date(),
})
    .strict();
export const listFeedRecordsSchema = carePageSchema;
// --- Stopwatch-to-Stable training attachment ---
const stopwatchSectionalInputSchema = z
    .object({
    sequence: z.number().int().positive(),
    distanceMeters: z.number().refine(isStopwatchSectionalDistanceMeters, {
        message: "distanceMeters must be a supported Stopwatch sectional distance.",
    }),
    cumulativeMilliseconds: z.number().int().nonnegative(),
    splitMilliseconds: z.number().int().nonnegative(),
})
    .strict();
export const attachStopwatchSessionSchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema,
    label: z.string().trim().min(1).max(120).nullable(),
    elapsedMilliseconds: z.number().int().positive(),
    sectionals: z.array(stopwatchSectionalInputSchema),
    occurredAt: z.coerce.date(),
})
    .strict()
    .superRefine((value, context) => {
    value.sectionals.forEach((sectional, index) => {
        if (sectional.sequence !== index + 1) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["sectionals", index, "sequence"],
                message: "Sectionals must be sequenced contiguously starting at 1.",
            });
        }
    });
});
export const listStopwatchSessionsSchema = carePageSchema;
// --- Training Scheduler (Lots, matrix entries, unified timeline) ---
const localDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "day must be an ISO calendar date (YYYY-MM-DD).");
export const listLotsSchema = z.object({ stableId: IdSchema }).strict();
export const addLotSchema = z
    .object({
    stableId: IdSchema,
    name: z.string().trim().min(1).max(LOT_NAME_MAX_LENGTH),
})
    .strict();
export const getTrainingBoardSchema = z
    .object({ stableId: IdSchema, day: localDateSchema })
    .strict();
const trainingDistanceSchema = z
    .object({
    value: z
        .number()
        .finite()
        .gt(TRAINING_DISTANCE_VALUE_MIN)
        .max(TRAINING_DISTANCE_VALUE_MAX),
    unit: DistanceUnitSchema,
})
    .strict();
export const trainingDistanceOrDurationSchema = z.discriminatedUnion("kind", [
    z
        .object({ kind: z.literal("DISTANCE"), distance: trainingDistanceSchema })
        .strict(),
    z
        .object({
        kind: z.literal("DURATION"),
        durationMilliseconds: z
            .number()
            .int()
            .positive()
            .max(TRAINING_DURATION_MILLISECONDS_MAX),
    })
        .strict(),
]);
const trainingEntryFieldsSchema = z.object({
    workType: z.string().trim().min(1).max(WORK_TYPE_MAX_LENGTH),
    distanceOrDuration: trainingDistanceOrDurationSchema,
    instructions: z
        .string()
        .trim()
        .min(1)
        .max(TRAINING_INSTRUCTIONS_MAX_LENGTH)
        .nullable(),
});
/** Not used standalone (only via `.shape`, spread into
 * `createTrainingEntrySchema`) — the placement-pair invariant is enforced by
 * that schema's own `.refine`, since `.extend(...).strict()` needs a plain
 * `ZodObject` shape to spread, not a `ZodEffects`. */
const placementSchema = z
    .object({
    lotId: IdSchema.nullable().default(null),
    riderHorsemanId: IdSchema.nullable().default(null),
})
    .strict();
export const createTrainingEntrySchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema,
    day: localDateSchema,
})
    .extend(trainingEntryFieldsSchema.shape)
    .extend(placementSchema.shape)
    .strict()
    .refine((value) => (value.lotId === null) === (value.riderHorsemanId === null), {
    message: "lotId and riderHorsemanId must both be set or both be null.",
    path: ["riderHorsemanId"],
});
export const bulkAssignTrainingEntriesSchema = z
    .object({
    stableId: IdSchema,
    horseIds: z.array(IdSchema).min(1).max(100),
    day: localDateSchema,
})
    .extend(trainingEntryFieldsSchema.shape)
    .strict();
export const moveTrainingEntrySchema = z
    .object({
    stableId: IdSchema,
    entryId: IdSchema,
    expectedVersion: z.number().int().positive(),
    targetLotId: IdSchema.nullable(),
    targetRiderHorsemanId: IdSchema.nullable(),
})
    .strict()
    .refine((value) => (value.targetLotId === null) === (value.targetRiderHorsemanId === null), {
    message: "targetLotId and targetRiderHorsemanId must both be set or both be null.",
    path: ["targetRiderHorsemanId"],
});
export const editTrainingEntrySchema = z
    .object({
    stableId: IdSchema,
    entryId: IdSchema,
    expectedVersion: z.number().int().positive(),
})
    .extend(trainingEntryFieldsSchema.shape)
    .strict();
export const logTrainingEntryOutcomeSchema = z
    .object({
    stableId: IdSchema,
    entryId: IdSchema,
    expectedVersion: z.number().int().positive(),
    outcomeNotes: z
        .string()
        .trim()
        .min(1)
        .max(TRAINING_OUTCOME_NOTES_MAX_LENGTH),
})
    .strict();
export const getTrainingEntryPrefillSchema = z
    .object({ stableId: IdSchema, horseId: IdSchema, day: localDateSchema })
    .strict();
export const listHorseTrainingTimelineSchema = carePageSchema;
// --- Vet Records / Farrier Records (Sealed, unified scheduled+outcome timeline) ---
const sealedRecordDocumentInputSchema = z
    .object({
    id: IdSchema,
    objectKey: z.string().trim().min(1).max(500),
    filename: z
        .string()
        .trim()
        .min(1)
        .max(SEALED_RECORD_DOCUMENT_FILENAME_MAX_LENGTH),
    contentType: z.enum(SEALED_RECORD_DOCUMENT_CONTENT_TYPES),
    size: z
        .number()
        .int()
        .positive()
        .max(SEALED_RECORD_DOCUMENT_MAX_SIZE_BYTES),
})
    .strict()
    .refine((value) => matchesSealedRecordDocumentExtension(value.contentType, value.filename), {
    message: "The file extension does not match its content type.",
    path: ["filename"],
});
const sealedRecordFieldsSchema = z.object({
    dueDate: localDateSchema.nullable(),
    scheduledNotes: z
        .string()
        .trim()
        .min(1)
        .max(SEALED_RECORD_NOTES_MAX_LENGTH)
        .nullable(),
    outcomeNotes: z
        .string()
        .trim()
        .min(1)
        .max(SEALED_RECORD_NOTES_MAX_LENGTH)
        .nullable(),
    documents: z
        .array(sealedRecordDocumentInputSchema)
        .max(SEALED_RECORD_DOCUMENT_MAX_COUNT),
});
/** A new Sealed record must carry a due date, an outcome, or both — never
 * neither (mirrors `horse_vet_records`/`horse_farrier_records`'s "empty
 * record" CHECK constraint). Documents only make sense alongside an outcome
 * — a still-open scheduled entry has nothing to attach yet. */
function checkSealedRecordFields(value, context) {
    if (value.dueDate === null && value.outcomeNotes === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["dueDate"],
            message: "Provide a due date, an outcome, or both.",
        });
    }
    if (value.outcomeNotes === null && value.documents.length > 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["documents"],
            message: "Documents can only be attached alongside an outcome.",
        });
    }
}
export const createVetRecordSchema = z
    .object({ stableId: IdSchema, horseId: IdSchema })
    .extend(sealedRecordFieldsSchema.shape)
    .strict()
    .superRefine(checkSealedRecordFields);
export const createFarrierRecordSchema = z
    .object({ stableId: IdSchema, horseId: IdSchema })
    .extend(sealedRecordFieldsSchema.shape)
    .strict()
    .superRefine(checkSealedRecordFields);
const logSealedRecordOutcomeFieldsSchema = z.object({
    outcomeNotes: z.string().trim().min(1).max(SEALED_RECORD_NOTES_MAX_LENGTH),
    documents: z
        .array(sealedRecordDocumentInputSchema)
        .max(SEALED_RECORD_DOCUMENT_MAX_COUNT),
});
export const logVetRecordOutcomeSchema = z
    .object({
    stableId: IdSchema,
    recordId: IdSchema,
    expectedVersion: z.number().int().positive(),
})
    .extend(logSealedRecordOutcomeFieldsSchema.shape)
    .strict();
export const logFarrierRecordOutcomeSchema = z
    .object({
    stableId: IdSchema,
    recordId: IdSchema,
    expectedVersion: z.number().int().positive(),
})
    .extend(logSealedRecordOutcomeFieldsSchema.shape)
    .strict();
export const listHorseVetRecordsSchema = carePageSchema;
export const listHorseFarrierRecordsSchema = carePageSchema;
export const beginSealedRecordDocumentUploadSchema = z
    .object({
    stableId: IdSchema,
    id: IdSchema,
    filename: z
        .string()
        .trim()
        .min(1)
        .max(SEALED_RECORD_DOCUMENT_FILENAME_MAX_LENGTH),
    contentType: z.enum(SEALED_RECORD_DOCUMENT_CONTENT_TYPES),
    contentLength: z
        .number()
        .int()
        .positive()
        .max(SEALED_RECORD_DOCUMENT_MAX_SIZE_BYTES),
})
    .strict()
    .refine((value) => matchesSealedRecordDocumentExtension(value.contentType, value.filename), {
    message: "The file extension does not match its content type.",
    path: ["filename"],
});
export const getSealedRecordDocumentSchema = z
    .object({ stableId: IdSchema, documentId: IdSchema })
    .strict();
// --- Vaccinations (Sealed, unified timeline plus vaccine-specific fields) ---
const vaccinationOutcomeFieldsSchema = z.object({
    dateAdministered: localDateSchema.nullable(),
    batchSerial: z.string().trim().min(1).max(80).nullable(),
    outcomeNotes: z
        .string()
        .trim()
        .min(1)
        .max(SEALED_RECORD_NOTES_MAX_LENGTH)
        .nullable(),
    nextDue: localDateSchema.nullable(),
    nextDueOverridden: z.boolean().default(false),
    documents: z
        .array(sealedRecordDocumentInputSchema)
        .max(SEALED_RECORD_DOCUMENT_MAX_COUNT),
});
/** Batch/serial, next-due, and documents only make sense alongside an actual
 * dose — mirrors `checkSealedRecordFields`'s "documents only make sense
 * alongside an outcome" rule, extended to vaccination's extra dose-only
 * fields. */
function checkVaccinationOutcomeFields(value, context) {
    if (value.dateAdministered !== null)
        return;
    const emptyOnly = [
        [
            "batchSerial",
            "Batch/serial can only be recorded alongside a date administered.",
        ],
        [
            "outcomeNotes",
            "Notes can only be recorded alongside a date administered.",
        ],
        [
            "nextDue",
            "A next-due date can only be recorded alongside a date administered.",
        ],
    ];
    for (const [field, message] of emptyOnly) {
        if (value[field] !== null) {
            context.addIssue({ code: z.ZodIssueCode.custom, path: [field], message });
        }
    }
    if (value.nextDueOverridden) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["nextDueOverridden"],
            message: "next_due cannot be overridden without a date administered.",
        });
    }
    if (value.documents.length > 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["documents"],
            message: "Documents can only be attached alongside a date administered.",
        });
    }
}
export const createVaccinationRecordSchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema,
    vaccineType: VaccineTypeCodeSchema,
    dueDate: localDateSchema.nullable(),
    scheduledNotes: z
        .string()
        .trim()
        .min(1)
        .max(SEALED_RECORD_NOTES_MAX_LENGTH)
        .nullable(),
})
    .extend(vaccinationOutcomeFieldsSchema.shape)
    .strict()
    .superRefine((value, context) => {
    if (value.dueDate === null && value.dateAdministered === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["dueDate"],
            message: "Provide a due date, a date administered, or both.",
        });
    }
    checkVaccinationOutcomeFields(value, context);
});
export const logVaccinationRecordOutcomeSchema = z
    .object({
    stableId: IdSchema,
    recordId: IdSchema,
    expectedVersion: z.number().int().positive(),
    dateAdministered: localDateSchema,
    batchSerial: z.string().trim().min(1).max(80).nullable(),
    outcomeNotes: z
        .string()
        .trim()
        .min(1)
        .max(SEALED_RECORD_NOTES_MAX_LENGTH)
        .nullable(),
    nextDue: localDateSchema.nullable(),
    nextDueOverridden: z.boolean().default(false),
    documents: z
        .array(sealedRecordDocumentInputSchema)
        .max(SEALED_RECORD_DOCUMENT_MAX_COUNT),
})
    .strict();
export const listHorseVaccinationRecordsSchema = carePageSchema;
export const listStableVaccinationAlertsSchema = z
    .object({ stableId: IdSchema })
    .strict();
const VaccinationScanKindSchema = z.enum(VACCINATION_SCAN_KINDS);
export const beginVaccinationScanUploadSchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema,
    id: IdSchema,
    filename: z
        .string()
        .trim()
        .min(1)
        .max(EXTRACTION_PHOTO_FILENAME_MAX_LENGTH),
    contentType: z.enum(EXTRACTION_PHOTO_CONTENT_TYPES),
    contentLength: z
        .number()
        .int()
        .positive()
        .max(EXTRACTION_PHOTO_MAX_SIZE_BYTES),
})
    .strict()
    .refine((value) => matchesExtractionPhotoExtension(value.contentType, value.filename), {
    message: "The file extension does not match its content type.",
    path: ["filename"],
});
export const scanVaccinationDocumentSchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema,
    id: IdSchema,
    kind: VaccinationScanKindSchema,
    objectKey: z.string().trim().min(1).max(1024),
    contentType: z.enum(EXTRACTION_PHOTO_CONTENT_TYPES),
    contentLength: z
        .number()
        .int()
        .positive()
        .max(EXTRACTION_PHOTO_MAX_SIZE_BYTES),
})
    .strict();
// --- Accounting (Sealed; Stable-wide bills -> allocation -> invoice) ---
const BillScanKindSchema = z.enum(BILL_SCAN_KINDS);
/** `BILL_RECEIPT` is the only extraction kind accepting either a photo or a
 * PDF (ADR 0017) — each with its own size cap
 * ({@link EXTRACTION_PHOTO_MAX_SIZE_BYTES}/{@link EXTRACTION_PDF_MAX_SIZE_BYTES}),
 * so the cap is checked with a `.refine` rather than a static `.max()`. */
const BILL_UPLOAD_CONTENT_TYPES = [
    ...EXTRACTION_PHOTO_CONTENT_TYPES,
    ...EXTRACTION_PDF_CONTENT_TYPES,
];
function maxSizeForBillUploadContentType(contentType) {
    return contentType === "application/pdf"
        ? EXTRACTION_PDF_MAX_SIZE_BYTES
        : EXTRACTION_PHOTO_MAX_SIZE_BYTES;
}
function matchesBillUploadExtension(contentType, filename) {
    return (matchesExtractionPhotoExtension(contentType, filename) ||
        matchesExtractionPdfExtension(contentType, filename));
}
export const beginBillScanUploadSchema = z
    .object({
    stableId: IdSchema,
    id: IdSchema,
    filename: z
        .string()
        .trim()
        .min(1)
        .max(EXTRACTION_PHOTO_FILENAME_MAX_LENGTH),
    contentType: z.enum(BILL_UPLOAD_CONTENT_TYPES),
    contentLength: z.number().int().positive(),
})
    .strict()
    .refine((value) => value.contentLength <= maxSizeForBillUploadContentType(value.contentType), {
    message: "The file is larger than allowed for its type.",
    path: ["contentLength"],
})
    .refine((value) => matchesBillUploadExtension(value.contentType, value.filename), {
    message: "The file extension does not match its content type.",
    path: ["filename"],
});
export const scanBillDocumentSchema = z
    .object({
    stableId: IdSchema,
    id: IdSchema,
    kind: BillScanKindSchema,
    objectKey: z.string().trim().min(1).max(1024),
    contentType: z.enum(BILL_UPLOAD_CONTENT_TYPES),
    contentLength: z.number().int().positive(),
})
    .strict()
    .refine((value) => value.contentLength <= maxSizeForBillUploadContentType(value.contentType), {
    message: "The file is larger than allowed for its type.",
    path: ["contentLength"],
});
function checkPositiveBillAmount(value, context) {
    if (BigInt(value.amountMinor) <= 0n) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["amountMinor"],
            message: "A bill's amount must be greater than zero.",
        });
    }
}
const billDescriptionSchema = z
    .string()
    .trim()
    .min(1)
    .max(SEALED_RECORD_NOTES_MAX_LENGTH)
    .nullable();
export const createBillSchema = z
    .object({
    stableId: IdSchema,
    vendor: z.string().trim().min(1).max(160),
    billDate: localDateSchema,
    description: billDescriptionSchema,
    category: ExpenseCategoryCodeSchema,
    amountMinor: AmountMinorSchema,
    currency: CurrencyCodeSchema,
    allocation: BillAllocationInputSchema,
    documents: z
        .array(sealedRecordDocumentInputSchema)
        .max(SEALED_RECORD_DOCUMENT_MAX_COUNT),
    extractionId: IdSchema.nullable().default(null),
})
    .strict()
    .superRefine(checkPositiveBillAmount);
export const updateBillSchema = z
    .object({
    stableId: IdSchema,
    billId: IdSchema,
    expectedVersion: z.number().int().positive(),
    vendor: z.string().trim().min(1).max(160),
    billDate: localDateSchema,
    description: billDescriptionSchema,
    category: ExpenseCategoryCodeSchema,
    amountMinor: AmountMinorSchema,
    currency: CurrencyCodeSchema,
    documents: z
        .array(sealedRecordDocumentInputSchema)
        .max(SEALED_RECORD_DOCUMENT_MAX_COUNT),
})
    .strict()
    .superRefine(checkPositiveBillAmount);
export const setBillAllocationSchema = z
    .object({
    stableId: IdSchema,
    billId: IdSchema,
    expectedVersion: z.number().int().positive(),
    allocation: BillAllocationInputSchema,
})
    .strict();
export const getBillSchema = z
    .object({ stableId: IdSchema, billId: IdSchema })
    .strict();
export const listBillsSchema = z
    .object({
    stableId: IdSchema,
    horseId: IdSchema.optional(),
    category: ExpenseCategoryCodeSchema.optional(),
    limit: z.number().int().min(1).max(100).default(50),
    cursor: paginationCursorSchema.nullable().default(null),
})
    .strict();
const invoiceGenerationTargetSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("HORSE"), horseId: IdSchema }).strict(),
    z.object({ kind: z.literal("OWNER"), ownerHorsemanId: IdSchema }).strict(),
]);
export const generateInvoiceDraftSchema = z
    .object({
    stableId: IdSchema,
    target: invoiceGenerationTargetSchema,
    dateRangeStart: localDateSchema,
    dateRangeEnd: localDateSchema,
    currency: CurrencyCodeSchema,
})
    .strict()
    .refine((value) => value.dateRangeStart <= value.dateRangeEnd, {
    message: "dateRangeEnd must not be before dateRangeStart.",
    path: ["dateRangeEnd"],
});
export const getInvoiceSchema = z
    .object({ stableId: IdSchema, invoiceId: IdSchema })
    .strict();
export const listInvoicesSchema = z
    .object({
    stableId: IdSchema,
    status: InvoiceStatusSchema.optional(),
    limit: z.number().int().min(1).max(100).default(50),
    cursor: paginationCursorSchema.nullable().default(null),
})
    .strict();
export const getSharedOwnerInvoiceSchema = z
    .object({ invoiceId: IdSchema })
    .strict();
export const listSharedOwnerInvoicesSchema = z
    .object({
    limit: z.number().int().min(1).max(100).default(50),
    cursor: paginationCursorSchema.nullable().default(null),
})
    .strict();
export const approveInvoiceSchema = z
    .object({
    stableId: IdSchema,
    invoiceId: IdSchema,
    expectedVersion: z.number().int().positive(),
})
    .strict();
export const shareInvoiceSchema = z
    .object({ stableId: IdSchema, invoiceId: IdSchema })
    .strict();
export const setAccountingAutoInvoiceEnabledSchema = z
    .object({ stableId: IdSchema, enabled: z.boolean() })
    .strict();
// --- Hire ---
const jobPostingFieldsSchema = z.object({
    title: z.string().trim().min(1).max(JOB_POSTING_TITLE_MAX_LENGTH),
    roleSought: EntityRoleSchema.nullable().default(null),
    description: z.string().trim().min(1).max(JOB_POSTING_DESCRIPTION_MAX_LENGTH),
});
export const createJobPostingSchema = z
    .object({
    stableId: IdSchema,
    clientMutationId: IdSchema,
    fields: jobPostingFieldsSchema,
})
    .strict();
export const updateJobPostingSchema = z
    .object({
    stableId: IdSchema,
    postingId: IdSchema,
    clientMutationId: IdSchema,
    expectedVersion: z.number().int().positive(),
    fields: jobPostingFieldsSchema,
})
    .strict();
export const closeJobPostingSchema = z
    .object({
    stableId: IdSchema,
    postingId: IdSchema,
    clientMutationId: IdSchema,
    expectedVersion: z.number().int().positive(),
})
    .strict();
export const reopenJobPostingSchema = z
    .object({
    stableId: IdSchema,
    postingId: IdSchema,
    clientMutationId: IdSchema,
    expectedVersion: z.number().int().positive(),
})
    .strict();
export const listJobPostingsForStableSchema = z
    .object({ stableId: IdSchema })
    .strict();
export const browseJobPostingsSchema = z
    .object({
    search: z.string().trim().min(1).max(160).optional(),
    roleSought: EntityRoleSchema.optional(),
    cursor: z.string().min(1).max(300).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
})
    .strict();
export const getJobPostingSchema = z.object({ postingId: IdSchema }).strict();
export const updateResumeSchema = z
    .object({
    bio: z.string().max(RESUME_BIO_MAX_LENGTH).nullable().default(null),
    skills: z
        .array(z.string().max(RESUME_SKILL_MAX_LENGTH))
        .max(RESUME_SKILLS_MAX_COUNT),
})
    .strict();
export const applyToPostingSchema = z
    .object({ postingId: IdSchema, clientMutationId: IdSchema })
    .strict();
export const withdrawApplicationSchema = z
    .object({
    applicationId: IdSchema,
    clientMutationId: IdSchema,
    expectedVersion: z.number().int().positive(),
})
    .strict();
export const decideApplicationSchema = z
    .object({
    stableId: IdSchema,
    applicationId: IdSchema,
    decision: z.enum(["ACCEPTED", "REJECTED"]),
    clientMutationId: IdSchema,
    expectedVersion: z.number().int().positive(),
})
    .strict();
export const listApplicationsForPostingSchema = z
    .object({ stableId: IdSchema, postingId: IdSchema })
    .strict();
export const getResumeSnapshotSchema = z
    .object({ stableId: IdSchema, applicationId: IdSchema })
    .strict();
const hireMessagePageQuerySchema = z.object({
    cursor: z.string().min(1).max(300).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const getHireConversationSchema = z
    .object({ conversationId: IdSchema })
    .merge(hireMessagePageQuerySchema)
    .strict();
export const listHireConversationsForStableSchema = z
    .object({ stableId: IdSchema })
    .merge(z.object({
    cursor: z.string().min(1).max(300).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
}))
    .strict();
export const sendHireMessageSchema = z
    .object({
    conversationId: IdSchema,
    body: z
        .string()
        .trim()
        .min(1)
        .max(2_000)
        .refine((body) => !/<[^>]+>/.test(body), "HTML is not accepted."),
    idempotencyKey: z.string().uuid(),
})
    .strict();
